import { useState, useRef, useEffect, useCallback } from 'react';
import * as Y from 'yjs';
import { WebsocketProvider } from 'y-websocket';
import { QuillBinding } from 'y-quill';
import { getWebSocketURL } from '../components/env';
import { getSupabase } from '../components/supabaseClient';
import typingDetection from '../utils/typingDetection';
import activeDocumentTracker from '../utils/activeDocumentTracker';
import globalPresenceManager from '../utils/globalPresenceManager';
import { royal_blue } from '../components/env';
import {
  analyzeTextDuplication,
  logDuplicationDebug,
} from '../utils/duplicationDebug.js';

/*
 * =============================================================================
 * COLLABORATION & WEBSOCKET — HOW THIS WORKS (PLAIN ENGLISH)
 * =============================================================================
 *
 * WHAT THIS HOOK DOES
 * -------------------
 * When someone opens a document in the rich-text editor, this hook wires that
 * editor to a shared real-time session so multiple people can edit the same
 * file at once. Under the hood it uses Yjs (a CRDT) for the document text,
 * y-websocket to talk to your collaboration server, and y-quill to keep
 * Quill and Yjs in sync. Cursors and “who is here” use Yjs *awareness*.
 *
 * WHERE THE WEBSOCKET LIVES
 * -------------------------
 * The browser does not open a random URL—it uses `getWebSocketURL()` from env
 * (your deployed y-websocket / compatible relay). The *room* is the
 * document’s `filePath`, URL-encoded. Everyone editing the same path joins the
 * same room; different files are different rooms.
 *
 * WHEN THE CONNECTION ACTUALLY STARTS
 * -------------------------------------
 * Setup only runs when: there is a `filePath`, the Quill editor exists, the
 * document is marked loaded, the editor has real content (not a blank doc),
 * and content loading has finished. A ref (`websocketSetupLock`) stops the
 * effect from starting two WebSocket setups at once (React strict mode /
 * re-renders).
 *
 * HOW A USER “JOINS”
 * ------------------
 * There is no separate “join room” REST call for the doc WebSocket. Joining
 * means: the effect runs, creates a `Y.Doc` and a `WebsocketProvider` with
 * `connect: true`, and the provider opens the WebSocket to the server. The
 * server forwards Yjs sync messages between everyone in that room.
 *
 * As soon as the provider exists, the code loads the signed-in user from
 * Supabase (session + optional `users.name`) and writes their display name,
 * stable color, and user id into *awareness* so other clients can show cursors
 * and avatars. Colors are cached (global presence manager + localStorage) so
 * the same user keeps a consistent color across tabs and refreshes.
 *
 * GLOBAL PRESENCE VS DOCUMENT WEBSOCKET
 * -------------------------------------
 * `globalPresenceManager` (and Supabase presence elsewhere) tracks who is on
 * which document across the app. This hook also checks that map *before* the
 * socket finishes connecting—if someone else is already on this file, the UI
 * can stay in a “syncing with others” state so a new user doesn’t type over an
 * empty copy while collaboration is still attaching.
 *
 * SYNC, SEEDING, AND THE QUILL BINDING
 * ------------------------------------
 * The collaboration server is treated as a *relay*: new clients may need to sync
 * with peers before they have the true document in Yjs. The hook waits for
 * `provider` sync when appropriate, can enter an “observer” mode (wait until
 * remote Yjs text arrives) instead of pushing local editor text into Yjs, and
 * only *seeds* Yjs from the local editor when it believes the user is truly
 * alone—otherwise two people opening at once could both seed and duplicate
 * content. After that decision, `QuillBinding` connects the shared `Y.Text`
 * named `quill` to the Quill instance so edits merge correctly.
 *
 * LIFECYCLE / LEAVING
 * -------------------
 * On cleanup (navigate away, file change, etc.) the hook tears down the
 * binding and disconnects the provider, clears timers, and releases the setup
 * lock. On disconnect it may trigger a save so work isn’t lost. Awareness
 * updates also feed grace-period logic so avatars don’t vanish the instant a
 * tab blips offline.
 *
 * =============================================================================
 */

/**
 * Generate a random color that avoids blue shades similar to royal_blue (#4169E1) and inactive blue (#3b82f6)
 * This prevents confusion between active WebSocket colors and inactive blue colors
 */
export function generateNonBlueColor() {
  const royalBlueRgb = { r: 0x41, g: 0x69, b: 0xE1 }; // #4169E1
  const inactiveBlueRgb = { r: 0x3b, g: 0x82, b: 0xf6 }; // #3b82f6
  
  const maxAttempts = 50;
  let attempts = 0;
  
  while (attempts < maxAttempts) {
    // Generate random RGB values
    const r = Math.floor(Math.random() * 256);
    const g = Math.floor(Math.random() * 256);
    const b = Math.floor(Math.random() * 256);
    
    // Calculate color distance from royal_blue and inactive blue
    const distToRoyalBlue = Math.sqrt(
      Math.pow(r - royalBlueRgb.r, 2) +
      Math.pow(g - royalBlueRgb.g, 2) +
      Math.pow(b - royalBlueRgb.b, 2)
    );
    
    const distToInactiveBlue = Math.sqrt(
      Math.pow(r - inactiveBlueRgb.r, 2) +
      Math.pow(g - inactiveBlueRgb.g, 2) +
      Math.pow(b - inactiveBlueRgb.b, 2)
    );
    
    // Avoid colors that are too similar to blue shades
    // Check if blue component is dominant and color is close to blue shades
    const isBlueDominant = b > r && b > g && b > 150;
    const isTooCloseToBlue = distToRoyalBlue < 80 || distToInactiveBlue < 80;
    
    // Accept color if it's not blue-dominant or too close to blue shades
    if (!isBlueDominant && !isTooCloseToBlue) {
      return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
    }
    
    attempts++;
  }
  
  // Fallback: return a non-blue color (purple/magenta range)
  const fallbackColors = [
    '#E91E63', // Pink
    '#9C27B0', // Purple
    '#F44336', // Red
    '#FF9800', // Orange
    '#4CAF50', // Green
    '#00BCD4', // Cyan (but lighter than blue)
    '#795548', // Brown
    '#607D8B', // Blue Grey (but distinct from royal blue)
  ];
  
  return fallbackColors[Math.floor(Math.random() * fallbackColors.length)];
}

/**
 * Wait until y-websocket has finished the initial Yjs sync with the server.
 * New clients should pull remote state before we seed Y.Text from the editor;
 * otherwise local (possibly stale/empty) content can overwrite the room for others.
 */
function waitForYjsProviderSynced(wsProvider, timeoutMs = 15000) {
  return new Promise((resolve) => {
    if (wsProvider?.synced) {
      resolve();
      return;
    }
    const done = () => {
      clearTimeout(timer);
      wsProvider.off("sync", onSync);
      resolve();
    };
    const onSync = (isSynced) => {
      if (isSynced) done();
    };
    wsProvider.on("sync", onSync);
    const timer = setTimeout(done, timeoutMs);
  });
}

/**
 * Joiners: wsProvider.synced can be true before remote Y.Text updates arrive — do not rely on
 * waitForYjsProviderSynced alone. Poll until Y.Text has content or timeout.
 */
function waitForYjsTextNonEmpty(ytext, timeoutMs = 15000) {
  return new Promise((resolve) => {
    if (ytext.length > 0) {
      resolve();
      return;
    }
    const start = Date.now();
    const id = setInterval(() => {
      if (ytext.length > 0) {
        clearInterval(id);
        resolve();
        return;
      }
      if (Date.now() - start >= timeoutMs) {
        clearInterval(id);
        resolve();
      }
    }, 120);
  });
}

/**
 * Custom hook for WebSocket collaboration and multi-user features
 * 
 * @param {Object} params - Hook parameters
 * @param {Object} params.editorRef - Reference to the editor component
 * @param {string} params.filePath - Path to the document file
 * @param {string} params.validId - Patient/schedule ID
 * @param {string} params.extractedName - Patient name
 * @param {boolean} params.documentLoaded - Whether document is loaded
 * @param {boolean} params.contentLoadingComplete - Whether content loading is complete
 * @param {string} params.correctedType - Patient type
 * @param {Function} params.setIsConnected - Setter for connection state
 * @param {Function} params.setConnectionStatus - Setter for connection status
 * @param {Function} params.setUserColors - Setter for user colors map
 * @param {Function} params.setHighlightedUser - Setter for highlighted user
 * @param {Function} params.setVersioningSystem - Setter for versioning system
 * @param {Function} params.handleSaveDoc - Function to save document
 * @param {Function} params.updateGlobalPresenceFromAwareness - Function to update presence
 * @param {Object} params.websocketSetupLock - Ref for WebSocket setup lock
 * @param {Object} params.versioningSystem - Versioning system instance
 * 
 * @returns {Object} Collaboration state and functions
 */
export function useCollaboration({
  editorRef,
  filePath,
  validId,
  actualPatientId, // The actual patient UUID from database (for presence tracking)
  extractedName,
  documentLoaded,
  contentLoadingComplete,
  correctedType,
  setIsConnected,
  setConnectionStatus,
  setUserColors,
  setHighlightedUser,
  highlightedUser, // Add highlightedUser to read current state
  setVersioningSystem,
  handleSaveDoc,
  updateGlobalPresenceFromAwareness,
  websocketSetupLock,
  versioningSystem,
  setIsLoadingDocument,
  isLoadingDocument,
  /** YYYY-MM-DD — scopes presence for recurring visits (same patient, different days). */
  scheduleDate = null,
}) {
  const bindingRef = useRef(null);
  const hasCompletedInitialJoinRef = useRef(false);
  /** Once false→true after editor loading overlay is released for this doc; blocks re-showing it on collab re-setup. */
  const suppressCollaborationLoadingOverlayRef = useRef(false);
  /** Mirrors the page-level isLoadingDocument so setEntryGateLoading can see the current value
   *  without being a dependency of the main WebSocket effect. If the page has already released
   *  loading (user sees content, toolbar was enabled), the collaboration layer must not re-lock. */
  const isLoadingDocumentRef = useRef(isLoadingDocument);
  useEffect(() => {
    isLoadingDocumentRef.current = isLoadingDocument;
    if (!isLoadingDocument) {
      suppressCollaborationLoadingOverlayRef.current = true;
    }
  }, [isLoadingDocument]);
  const [provider, setProvider] = useState(null);
  // Store functions in refs to avoid dependency issues
  const handleSaveDocRef = useRef(handleSaveDoc);
  const updateGlobalPresenceRef = useRef(updateGlobalPresenceFromAwareness);
  
  // Update refs when functions change
  useEffect(() => {
    handleSaveDocRef.current = handleSaveDoc;
    updateGlobalPresenceRef.current = updateGlobalPresenceFromAwareness;
  }, [handleSaveDoc, updateGlobalPresenceFromAwareness]);

  // Reset initial-entry gate when opening a different document.
  useEffect(() => {
    hasCompletedInitialJoinRef.current = false;
    suppressCollaborationLoadingOverlayRef.current = false;
  }, [filePath]);

  // WebSocket collaboration setup
  useEffect(() => {
    if (!filePath) {
      return;
    }
    const editor = editorRef.current?.getEditor(0);
    
    if (!editor || !documentLoaded) {
      return;
    }

    const setEntryGateLoading = (isLoading) => {
      if (!setIsLoadingDocument) return;
      // Only block during first entry; never re-block users already in-doc.
      const hasActiveBinding =
        bindingRef.current && typeof bindingRef.current.destroy === 'function';
      if (isLoading && (hasCompletedInitialJoinRef.current || hasActiveBinding)) return;
      if (isLoading && suppressCollaborationLoadingOverlayRef.current) {
        logDuplicationDebug('loading-gate-blocked-post-release', {
          reason:
            'Collaboration asked to show loading after this document already released the editor — ignored so a second user joining or WebSocket re-setup does not cover the page.',
          filePath,
        });
        return;
      }
      // Page-level content load may have set isLoadingDocument=false directly (bypassing
      // setEntryGateLoading). If the user can already see the doc and type, do not re-grey
      // the toolbar just because we're waiting for remote Y.Text sync.
      if (isLoading && isLoadingDocumentRef.current === false) {
        logDuplicationDebug('loading-gate-blocked-page-already-released', {
          reason:
            'Collaboration requested loading=true but page-level isLoadingDocument is already false — user is viewing content and able to edit; re-locking would grey out the toolbar.',
          filePath,
        });
        suppressCollaborationLoadingOverlayRef.current = true;
        return;
      }
      if (!isLoading) {
        suppressCollaborationLoadingOverlayRef.current = true;
      }
      setIsLoadingDocument(isLoading);
    };

    const hasSubstantialContent = editor.getLength() > 1;
    if (!hasSubstantialContent) {
      // New/empty document — no collaboration sync needed, release loading immediately
      if (contentLoadingComplete) {
        setEntryGateLoading(false);
        hasCompletedInitialJoinRef.current = true;
      }
      return;
    }

    if (!contentLoadingComplete) {
      return;
    }

    if (websocketSetupLock.current) {
      logDuplicationDebug('ws-setup-skipped', {
        reason:
          'websocketSetupLock is already held — a second collaboration setup was blocked (React Strict Mode double effect, rapid dependency changes, or overlapping runs). If content later duplicates, suspect two lifecycles or a race with content load.',
        filePath,
      });
      return;
    }
    websocketSetupLock.current = true;

    // Only log once per setup
    if (!window.__wsSetupLogged) {
      console.log('🔌 WebSocket: Setting up collaboration for document:', filePath);
      window.__wsSetupLogged = true;
    }

    let wsProvider = null;
    let isCleaningUp = false;
    let typingCleanupInterval = null;
    let connectionTimeout = null;
    let cursorNameFixInterval = null;
    /** @type {null | (() => void)} */
    let duplicateMonitorCleanup = null;
    /** Release loading overlay if observer mode never obtains a QuillBinding */
    let observerStuckReleaseTimer = null;

    const clearObserverStuckTimer = () => {
      if (observerStuckReleaseTimer) {
        clearTimeout(observerStuckReleaseTimer);
        observerStuckReleaseTimer = null;
      }
    };
    const setLocalSyncReady = (isReady) => {
      try {
        if (wsProvider?.awareness) {
          wsProvider.awareness.setLocalStateField('doc_sync_ready', !!isReady);
        }
      } catch (_) {
        // ignore
      }
    };

    const ydoc = new Y.Doc();
    const ytext = ydoc.getText('quill');
    let lastYTextLenForDebug = 0;
    
    // Helper to dispatch collab telemetry event for CollaborationDebugPanel
    const dispatchTelemetry = (direction, eventType, bytes, userId = null, extra = {}) => {
      window.dispatchEvent(new CustomEvent('collab-telemetry', {
        detail: {
          direction,
          event: eventType,
          bytes,
          userId,
          ts: Date.now(),
          ...extra
        }
      }));
    };
    
    ytext.observe((event) => {
      const len = ytext.length;
      
      // Determine if local or remote by checking transaction origin
      // In Yjs, transaction.origin === null means local change made directly
      // transaction.origin === wsProvider means remote change received via WebSocket
      const origin = event.transaction?.origin;
      const isLocal = origin === null || origin === undefined;
      const direction = isLocal ? 'sent' : 'received';
      
      // Estimate bytes from delta operations
      let bytes = 0;
      if (event.delta) {
        event.delta.forEach(op => {
          if (op.insert) {
            if (typeof op.insert === 'string') bytes += op.insert.length;
            else bytes += 50; // estimate for embeds/objects
          }
          if (op.delete) bytes += op.delete;
          if (op.retain) bytes += op.retain;
        });
      }
      bytes = Math.max(bytes, 1); // Minimum 1 byte
      
      // Dispatch telemetry for CollaborationDebugPanel
      dispatchTelemetry(direction, 'yjs-update', bytes);
      if (
        lastYTextLenForDebug > 800 &&
        len > lastYTextLenForDebug * 1.5 &&
        len - lastYTextLenForDebug > lastYTextLenForDebug * 0.35
      ) {
        logDuplicationDebug('ytext-length-surge', {
          reason:
            'Y.Text jumped by a large fraction in one update — often concurrent seed, reconnect applying remote state over local, or very large doc syncing in chunks; compare with websocket status and awareness.',
          filePath,
          previousLength: lastYTextLenForDebug,
          newLength: len,
          deltaChars: len - lastYTextLenForDebug,
        });
      }
      lastYTextLenForDebug = len;
    });
    const encodedRoom = encodeURIComponent(filePath);
    const wsUrl = getWebSocketURL();
    
    if (!wsUrl || wsUrl === 'undefined' || wsUrl === 'null') {
      console.error('❌ Invalid WebSocket URL:', wsUrl);
      setIsConnected(false);
      websocketSetupLock.current = false;
      setEntryGateLoading(false);
      return;
    }
    
    const setupWebSocket = () => {
      if (isCleaningUp) return;

      const installDuplicateMonitor = () => {
        if (duplicateMonitorCleanup) {
          duplicateMonitorCleanup();
        }
        let lastPlainLen = 0;
        let debounceTimer = null;
        const onTextChange = () => {
          clearTimeout(debounceTimer);
          debounceTimer = setTimeout(() => {
            if (isCleaningUp) return;
            try {
              const plain = editor.getText?.() ?? '';
              const len = plain.length;
              const analysis = analyzeTextDuplication(plain);
              if (analysis.suspected) {
                logDuplicationDebug('suspected-duplicate-live-editor', {
                  reason: analysis.reason,
                  pattern: analysis.pattern,
                  editorPlainLength: len,
                  ytextLength: ytext.length,
                  filePath,
                  note:
                    'Detected on this client after local/remote edit — often the already-connected user when a late joiner merged a full second copy into Yjs.',
                });
              }
              if (lastPlainLen > 800 && len > lastPlainLen * 1.75) {
                logDuplicationDebug('suspected-duplicate-length-jump', {
                  reason:
                    'Plain text length jumped sharply — consistent with merging a full duplicate from another client.',
                  previousLength: lastPlainLen,
                  newLength: len,
                  filePath,
                });
              }
              lastPlainLen = len;
            } catch (_) {
              /* ignore */
            }
          }, 400);
        };
        editor.on('text-change', onTextChange);
        duplicateMonitorCleanup = () => {
          clearTimeout(debounceTimer);
          try {
            editor.off('text-change', onTextChange);
          } catch (_) {
            /* ignore */
          }
        };
      };

      // Pre-emptive loading lock: check globalPresenceManager for OTHER users before WS connects.
      // This closes the gap between document content loading (isLoadingDocument=false) and
      // the async binding setup that detects other users. Without this, User B can type during
      // the WS connect + bindingDelay window (hundreds of ms to seconds on slow networks).
      // Runs async (needs current user ID to filter self) but is fast — session is cached.
      (async () => {
        try {
          const supabase = await getSupabase();
          const { data: { session } } = await supabase.auth.getSession();
          const currentUserId = session?.user?.id;
          const othersOnDoc = Array.from(globalPresenceManager.activeUsers.values())
            .filter(u => u.document_path === filePath && u.user_id !== currentUserId);
          if (othersOnDoc.length > 0 && !isCleaningUp) {
            logDuplicationDebug('preemptive-loading-lock', {
              reason:
                'Global presence shows other user(s) on this document path before WS binding — reduces typing on stale editor before Yjs attaches; bad timing here plus seed can duplicate content.',
              otherUserCount: othersOnDoc.length,
              filePath,
            });
            setConnectionStatus('syncing-with-others');
            setEntryGateLoading(true);
          }
        } catch (e) { /* ignore — full async check happens at bindingDelay */ }
      })();

      try {
        setConnectionStatus('connecting');
        setIsConnected(true);
        
        connectionTimeout = setTimeout(() => {
          if (!isCleaningUp) {
            // Reduced logging - only log if actually stuck
          }
        }, 5000);
        
        wsProvider = new WebsocketProvider(
          wsUrl,
          encodedRoom,
          ydoc,
          {
            connect: true,
            maxBackoffTime: 5000,
            disableBc: true
          }
        );
        
        window.wsProvider = wsProvider;
        setProvider(wsProvider);

        // CRITICAL: Set user awareness IMMEDIATELY when WebSocket provider is created
        // This ensures user info is available before QuillBinding creates cursors
        // This is especially important for slow networks (3G) where delays can cause issues
        (async () => {
          try {
            const supabase = await getSupabase();
            const { data: { session } } = await supabase.auth.getSession();
            const user = session?.user;
            
            if (user && wsProvider && wsProvider.awareness) {
              const { data: userData } = await supabase
                .from('users')
                .select('name')
                .eq('id', user.id)
                .single();
              
              const userName = userData?.name || user.email || 'Anonymous';
              // Use cached color if available (for returning users), otherwise check localStorage,
              // otherwise generate new one. localStorage persists across page refreshes/new tabs.
              const cachedColor = globalPresenceManager.getCachedUserColor(user.id);
              const localStorageColor = !cachedColor ? localStorage.getItem(`userColor_${user.id}`) : null;
              const userColor = cachedColor || localStorageColor || generateNonBlueColor();

              // Cache the color for future use (in case they disconnect and reconnect)
              if (!cachedColor) {
                globalPresenceManager.cacheUserColor(user.id, userColor);
              }
              // Always persist to localStorage so color survives page refreshes/new tabs
              localStorage.setItem(`userColor_${user.id}`, userColor);

              // Set user awareness immediately, even before WebSocket connects
              // This ensures the info is available when QuillBinding creates cursors
              wsProvider.awareness.setLocalStateField('user', {
                name: userName,
                color: userColor,
                id: user.id
              });
              
              wsProvider.awareness.setLocalStateField('is_typing', false);
              wsProvider.awareness.setLocalStateField('cursor_active', false);
              wsProvider.awareness.setLocalStateField('doc_sync_ready', false);
              
              console.log('✅ User awareness set immediately:', { userName, userId: user.id });
            }
          } catch (error) {
            console.error('❌ Error setting up user awareness early:', error);
          }
        })();

        // Set up periodic cleanup of stale typing status
        typingCleanupInterval = setInterval(() => {
          try {
            import('../utils/typingDetection').then(({ default: typingDetection }) => {
              if (typingDetection && typingDetection.forceClearAllTypingStatus) {
                typingDetection.forceClearAllTypingStatus();
              }
            }).catch(error => {
              console.error('❌ Error importing typing detection for cleanup:', error);
            });
          } catch (error) {
            console.error('❌ Error in typing cleanup interval:', error);
          }
        }, 15000);

        // Single status handler to avoid duplicate listeners
        wsProvider.on('status', ({ status }) => {
          if (isCleaningUp) return;

          if (status === 'disconnected' || status === 'connected') {
            logDuplicationDebug('ws-status', {
              reason:
                status === 'disconnected'
                  ? 'WebSocket disconnected — save may run; if the editor reconnects and seeds again while another copy exists in Yjs, duplication can occur.'
                  : 'WebSocket connected — initial or reconnect; reconnect after partial sync is a common duplicate-seed scenario for flaky clients.',
              status,
              filePath,
              ytextLength: ytext?.length,
            });
          }

          setConnectionStatus(status);
          
          // Dispatch collab-telemetry event for CollaborationDebugPanel
          window.dispatchEvent(new CustomEvent('collab-telemetry', {
            detail: {
              type: 'connection',
              status,
              ts: Date.now(),
            }
          }));
          
          if (status === 'disconnected') {
            // Save document on disconnect
            if (handleSaveDocRef.current) {
              setTimeout(() => {
                if (!isCleaningUp) {
                  handleSaveDocRef.current();
                }
              }, 2000);
            }
            setTimeout(() => {
              if (status === 'disconnected' && !isCleaningUp) {
                setIsConnected(false);
              }
            }, 3000);
          } else if (status === 'connecting') {
            setIsConnected(true);
          }
          
          if (status === 'connected') {
            setIsConnected(true);
            if (connectionTimeout) {
              clearTimeout(connectionTimeout);
              connectionTimeout = null;
            }
            
            // CRITICAL: Force awareness sync when WebSocket connects
            // This ensures user info is broadcast to other clients, especially important for slow networks
            setTimeout(() => {
              try {
                if (wsProvider && wsProvider.awareness) {
                const currentState = wsProvider.awareness.getLocalState();
                if (currentState?.user) {
                  // Force update to ensure awareness is synced
                  wsProvider.awareness.setLocalStateField('user', currentState.user);
                  console.log('✅ Forced awareness sync on WebSocket connect:', { userName: currentState.user.name });

                  // IMMEDIATE: Update global presence right away so avatars light up immediately
                  // This bypasses the throttled awareness change handlers
                  const states = wsProvider.awareness.getStates();
                  if (states.size > 0) {
                    // Extract and cache colors immediately
                    const immediateColors = new Map();
                    states.forEach((state, clientId) => {
                      if (state.user && state.user.color && state.user.id) {
                        immediateColors.set(state.user.id, state.user.color);
                      }
                    });
                    if (immediateColors.size > 0) {
                      globalPresenceManager.cacheUserColors(immediateColors);
                      // Also update setUserColors directly for immediate display
                      setUserColors(prevColors => {
                        const newColors = new Map(prevColors);
                        immediateColors.forEach((color, id) => newColors.set(id, color));
                        return newColors;
                      });
                      console.log('🎨 Immediately cached and set user colors on connect:', immediateColors.size);
                    }

                    if (updateGlobalPresenceRef.current) {
                      console.log('🎨 Immediately updating global presence for avatar colors:', { statesCount: states.size });
                      updateGlobalPresenceRef.current(states);
                    }
                  }
                  }
                }
              } catch (error) {
                console.warn('⚠️ Error forcing awareness sync:', error);
              }
            }, 100);
            
            if (!bindingRef.current) {
              const hostname = window.location.hostname;
              const bindingDelay = hostname === 'localhost' ? 200 : 500;

              setTimeout(async () => {
                if (!bindingRef.current && !isCleaningUp) {
                  const t0 = Date.now();
                  console.log('[DUPLICATION DEBUG] Binding setup started', {
                    filePath,
                    wsProviderSynced: wsProvider?.synced,
                    ytextLength: ytext.length,
                    hostname,
                    bindingDelay,
                    ts: new Date().toISOString(),
                  });

                  // ─── Helper: check awareness for other clients ───────────────
                  const getOtherClients = () => {
                    if (!wsProvider?.awareness) return { count: 0, found: false };
                    const states = wsProvider.awareness.getStates();
                    const localId = wsProvider.awareness.clientID;
                    let found = false;
                    states.forEach((state, clientId) => {
                      if (clientId !== localId && state.user) found = true;
                    });
                    return { count: states.size, found };
                  };

                  const logEditorDuplicationRisk = (phase, extra = {}) => {
                    try {
                      const plain = editor.getText?.() ?? '';
                      const analysis = analyzeTextDuplication(plain);
                      if (analysis.suspected) {
                        logDuplicationDebug(phase, {
                          ...analysis,
                          ...extra,
                          editorPlainLength: plain.length,
                          ytextLength: ytext.length,
                          filePath,
                        });
                      }
                    } catch (e) {
                      logDuplicationDebug('duplicate-check-error', {
                        message: e?.message,
                        filePath,
                      });
                    }
                  };

                  // ─── Helper: enter observer mode ─────────────────────────────
                  // Waits for Yjs content to arrive from the server/other clients
                  // before creating the QuillBinding. Never seeds ytext locally.
                  // allowLeaderSeed: only true for solo relay deadlock (ws-not-synced-after-wait).
                  // Throttled joiners can have awareness={self} while global presence shows the peer —
                  // leader seed then inserts a full second copy and duplicates the doc in the CRDT.
                  const enterObserverMode = (reason, { allowLeaderSeed = false } = {}) => {
                    const othersNow = getOtherClients().found;
                    logDuplicationDebug('observer-mode', {
                      reason: `Entering observer mode (${reason}) — local client will NOT seed until Y.Text receives remote content or fallback runs. If this fires incorrectly, duplicate seeds can happen elsewhere.`,
                      observerReason: reason,
                      allowLeaderSeed,
                      ytextLength: ytext.length,
                      elapsedMs: Date.now() - t0,
                      filePath,
                      awarenessOthers: othersNow,
                    });
                    console.log(`[DUPLICATION DEBUG] Entering observer mode (reason: ${reason})`, {
                      ytextLength: ytext.length,
                      elapsed: Date.now() - t0,
                      allowLeaderSeed,
                    });
                    let observerActive = true;
                    let observerFallbackTimer = null;
                    /** When both peers sit at ytext=0, lowest awareness clientID seeds after 4s (breaks deadlock). */
                    let leaderObserverSeedTimer = null;
                    let ytextObserverRemoved = false;
                    const createBindingWhenSynced = () => {
                      const hasRealBinding = bindingRef.current && typeof bindingRef.current.destroy === 'function';
                      if (!observerActive || hasRealBinding || isCleaningUp) return;
                      if (ytext.length > 0) {
                        observerActive = false;
                        if (observerFallbackTimer) {
                          clearTimeout(observerFallbackTimer);
                          observerFallbackTimer = null;
                        }
                        if (leaderObserverSeedTimer) {
                          clearTimeout(leaderObserverSeedTimer);
                          leaderObserverSeedTimer = null;
                        }
                        if (!ytextObserverRemoved) {
                          try {
                            ytext.unobserve(createBindingWhenSynced);
                          } catch (_) {
                            /* yjs may throw if already removed */
                          }
                          ytextObserverRemoved = true;
                        }
                        console.log(`[DUPLICATION DEBUG] Observer: Yjs content arrived (${ytext.length} chars), creating binding in 200ms`, {
                          elapsed: Date.now() - t0,
                        });
                        // Defer one tick so we're not inside a Yjs transaction
                        setTimeout(() => {
                          const hasBindingNow = bindingRef.current && typeof bindingRef.current.destroy === 'function';
                          if (hasBindingNow || isCleaningUp || !wsProvider || !wsProvider.awareness) {
                            console.log('[DUPLICATION DEBUG] Observer binding skipped (already bound or cleaning up)');
                            return;
                          }
                          try {
                            const binding = new QuillBinding(ytext, editor, wsProvider.awareness);
                            bindingRef.current = binding;
                            setConnectionStatus('connected');
                            setEntryGateLoading(false);
                            hasCompletedInitialJoinRef.current = true;
                            setLocalSyncReady(true);
                            console.log(`[DUPLICATION DEBUG] ✅ Observer binding created. ytext=${ytext.length} chars, elapsed=${Date.now() - t0}ms`);
                            logEditorDuplicationRisk('suspected-duplicate-after-observer-binding', {
                              branch: 'observer-ytext-arrived',
                            });
                            installDuplicateMonitor();
                            clearObserverStuckTimer();
                          } catch (err) {
                            console.error('[DUPLICATION DEBUG] Observer binding error:', err);
                          }
                        }, 200);
                      }
                    };
                    // Sentinel prevents WS reconnect from triggering a second binding setup.
                    bindingRef.current = 'observing';
                    ytext.observe(createBindingWhenSynced);

                    // If every client is stuck in observer with empty Y.Text (relay never synced, or
                    // presence blocked the first user from seeding), the room never gets CRDT state.
                    // Lowest awareness clientID seeds once — peers then receive updates via Yjs.
                    //
                    // Two cases trigger this seed:
                    //   (a) SOLO relay deadlock: ids.length === 1 AND allowLeaderSeed — legacy path.
                    //   (b) MULTI-CLIENT both-empty deadlock: ids.length > 1, everyone hit observer,
                    //       nobody ever seeded. The lowest clientID breaks the tie, regardless of
                    //       allowLeaderSeed — otherwise both users sit in observer forever with the
                    //       editor usable but no QuillBinding (so typing never reaches Y.Text).
                    //
                    // 4s (previously 12s): the editor is read-only for the whole observer window
                    // (setEntryGateLoading(true) at the observer entries), so a 12s seed timer
                    // translates to 12s of "can't type" for the user. 4s gives late Yjs sync
                    // traffic time to arrive (createBindingWhenSynced will fire first if so and
                    // cancel this timer); if nothing arrives, it's the deadlock case and we want
                    // to unstick as fast as possible.
                    leaderObserverSeedTimer = setTimeout(() => {
                      if (!observerActive || isCleaningUp) return;
                      const hasReal = bindingRef.current && typeof bindingRef.current.destroy === 'function';
                      if (hasReal) return;
                      if (ytext.length > 0) return;
                      if (!wsProvider?.awareness || !editor) return;

                      const states = wsProvider.awareness.getStates();
                      const ids = Array.from(states.keys()).sort((a, b) => a - b);
                      const localId = wsProvider.awareness.clientID;
                      const isSolo = ids.length === 1;
                      const isLeader = ids.length > 0 && localId === ids[0];

                      if (isSolo) {
                        // Solo case: original behavior — only seed if this observer entry opted in.
                        if (!allowLeaderSeed) {
                          logDuplicationDebug('observer-leader-seed-disabled', {
                            reason:
                              'Leader seed from editor is disabled for this observer entry (joiner / presence paths — avoids duplicating when awareness lags throttled peers).',
                            observerReason: reason,
                            filePath,
                          });
                          return;
                        }
                        // isLeader is always true when solo — fall through to seed.
                      } else {
                        // Multi-client case: break the both-empty deadlock by electing the lowest clientID.
                        // Do NOT require allowLeaderSeed — the whole point is to recover observer paths
                        // that defensively set it to false and then got stuck because every peer also
                        // refuses to seed.
                        if (!isLeader) {
                          logDuplicationDebug('observer-leader-seed-skip-not-leader-multi', {
                            reason:
                              'Multi-client both-empty deadlock — not the lowest clientID; wait for peer to seed.',
                            observerReason: reason,
                            allowLeaderSeed,
                            localId,
                            sortedAwarenessIds: ids,
                            filePath,
                          });
                          return;
                        }
                        logDuplicationDebug('observer-leader-seed-multi-client-break-deadlock', {
                          reason:
                            'All awareness clients stuck at ytext=0 after 4s — lowest clientID seeds from editor to break the deadlock (no peer will ever forward CRDT state otherwise).',
                          observerReason: reason,
                          allowLeaderSeed,
                          localId,
                          sortedAwarenessIds: ids,
                          awarenessClientCount: ids.length,
                          filePath,
                        });
                      }

                      void (async () => {
                        // Solo path only: bail if Supabase presence lists other users on this path
                        // (they may already hold Yjs that hasn't reached us yet). For the multi-client
                        // deadlock, the peer being in global presence is EXPECTED — skipping here would
                        // re-introduce the exact deadlock we're trying to break.
                        if (isSolo) {
                          try {
                            const supabase = await getSupabase();
                            const { data: { session } } = await supabase.auth.getSession();
                            const uid = session?.user?.id;
                            if (uid) {
                              const othersOnPath = Array.from(globalPresenceManager.activeUsers.values()).filter(
                                (u) => u.document_path === filePath && u.user_id !== uid,
                              );
                              if (othersOnPath.length > 0) {
                                logDuplicationDebug('observer-leader-seed-skip-global-presence', {
                                  reason:
                                    'Global presence lists another user on this filePath — do not insert a full editor copy into Y.Text (would duplicate CRDT state).',
                                  otherCount: othersOnPath.length,
                                  filePath,
                                });
                                return;
                              }
                            }
                          } catch (_) {
                            /* ignore */
                          }
                        }

                        if (!observerActive || isCleaningUp) return;
                        if (ytext.length > 0) return;
                        if (!editor || editor.getLength() <= 1) {
                          logDuplicationDebug('observer-leader-seed-skip-empty-editor', { filePath });
                          return;
                        }

                        logDuplicationDebug('observer-leader-seed', {
                          reason: isSolo
                            ? 'Y.Text still empty after 4s in observer — solo relay deadlock only; seeding from editor.'
                            : 'Y.Text still empty after 4s in multi-client observer — lowest clientID breaks the both-empty deadlock by seeding from editor.',
                          multiClient: !isSolo,
                          filePath,
                          editorLength: editor.getLength(),
                        });
                        console.log('[DUPLICATION DEBUG] Observer leader seed from editor → Y.Text', {
                          multiClient: !isSolo,
                          awarenessClientCount: ids.length,
                        });

                        try {
                          const currentContent = editor.getContents();
                          if (currentContent?.ops?.length > 0) {
                            currentContent.ops.forEach((op) => {
                              if (op.insert) {
                                if (typeof op.insert === 'string') {
                                  ytext.insert(ytext.length, op.insert, op.attributes || {});
                                } else if (op.insert && typeof op.insert === 'object') {
                                  ytext.insertEmbed(ytext.length, op.insert, {});
                                }
                              }
                            });
                          }
                        } catch (e) {
                          console.error('[DUPLICATION DEBUG] Observer leader seed failed:', e);
                        }
                      })();
                    }, 4000);

                    // 5s reminder: for joiner/presence observer paths we do not leader-seed (see allowLeaderSeed).
                    // Solo relay deadlock (ws-not-synced-after-wait) may still run leader seed at 4s.
                    observerFallbackTimer = setTimeout(() => {
                      const hasRealBinding = bindingRef.current && typeof bindingRef.current.destroy === 'function';
                      if (!observerActive || hasRealBinding || isCleaningUp) return;
                      if (ytext.length > 0) return;
                      if (!wsProvider?.awareness) return;
                      if (allowLeaderSeed) return;

                      logDuplicationDebug('observer-fallback-skipped-leader-seed', {
                        reason:
                          'Leader seed from editor is disabled for this observer entry — wait for remote Y.Text only (avoids racing slow sync and duplicating the peer\'s copy).',
                        observerReason: reason,
                        filePath,
                      });
                      console.log(
                        `[DUPLICATION DEBUG] Observer fallback: skip leader seed (observerReason=${reason}) — keep waiting for remote Yjs`,
                      );
                    }, 5000);

                    clearObserverStuckTimer();
                    observerStuckReleaseTimer = setTimeout(() => {
                      if (isCleaningUp) return;
                      const b = bindingRef.current;
                      const stillObserving = b === 'observing';
                      const noRealBinding = !b || typeof b.destroy !== 'function';
                      if (stillObserving || noRealBinding) {
                        logDuplicationDebug('observer-stuck-releasing-loading-overlay', {
                          reason:
                            'observer mode still had no QuillBinding after 45s — releasing loading overlay so the editor is usable; collaboration may still sync in the background.',
                          observerReason: reason,
                          filePath,
                        });
                        console.log('[DUPLICATION DEBUG] Observer stuck timeout — releasing loading overlay', {
                          observerReason: reason,
                          filePath,
                        });
                        setEntryGateLoading(false);
                      }
                    }, 45000);
                  };

                  // ─── Step 1: Initial awareness check ─────────────────────────
                  let { count: awarenessCount, found: otherClientsConnected } = getOtherClients();
                  console.log(`[DUPLICATION DEBUG] Initial awareness check: totalStates=${awarenessCount}, otherUsers=${otherClientsConnected}, ytextLength=${ytext.length}`);

                  // ─── Step 2: Branch on whether other users are present ────────
                  let shouldPopulateYjs = true;

                  if (otherClientsConnected) {
                    // ── Other users ARE in awareness ──────────────────────────
                    console.log(`[DUPLICATION DEBUG] Other users detected in awareness. ytextLength=${ytext.length}`);
                    setConnectionStatus('syncing-with-others');
                    setEntryGateLoading(true);

                    if (ytext.length > 0) {
                      // Yjs already has their content — bind immediately (setContents will sync editor)
                      console.log(`[DUPLICATION DEBUG] Yjs already has content (${ytext.length} chars) — binding directly`);
                      shouldPopulateYjs = false;
                      // fall through to QuillBinding creation below
                    } else {
                      // Give the relay / peer time to exchange Yjs state before locking into observer.
                      // Otherwise both users can sit at ytext=0 forever (first user still in 15s wait, second enters observer).
                      console.log(
                        `[DUPLICATION DEBUG] Joiner: waiting up to 15s for Y.Text from peer (synced can be true before remote CRDT applies)...`,
                      );
                      await waitForYjsTextNonEmpty(ytext, 15000);
                      if (!wsProvider || isCleaningUp) return;
                      if (ytext.length > 0) {
                        console.log(
                          `[DUPLICATION DEBUG] Joiner: remote Yjs arrived (${ytext.length} chars) after wait — binding`,
                        );
                        shouldPopulateYjs = false;
                        // fall through to QuillBinding creation below
                      } else {
                        enterObserverMode('other-users-ytext-empty');
                        return;
                      }
                    }

                  } else {
                    // ── No other users in awareness yet ──────────────────────
                    // The server is a pure relay (no server-side Yjs state). wsProvider.synced
                    // only fires when another connected client responds to our SyncStep1.
                    // If no one is connected, this times out (solo / new doc path).
                    //
                    // 3s (previously 15s): on localhost-solo the wait never resolves normally,
                    // so the timeout IS the path. Keeping it short means User A seeds fast and
                    // is fully bound before User B joins — that matters because entering
                    // observer mode AFTER an initial unlocked period locks the editor
                    // (setEntryGateLoading(true) at the observer branches), producing a
                    // visible "can't type" window while a peer syncs. Real networks with live
                    // peers report sync in <1s; 3s is comfortable. If a peer exists but sync
                    // is slow, the awareness + presence checks below still correctly avoid
                    // solo-seeding into an already-live room.
                    console.log(`[DUPLICATION DEBUG] No other users in awareness — waiting for Yjs sync (relay server, 3s timeout)...`);
                    await waitForYjsProviderSynced(wsProvider, 3000);

                    const elapsed1 = Date.now() - t0;
                    console.log(`[DUPLICATION DEBUG] waitForYjsProviderSynced resolved. ytextLength=${ytext.length}, wsProviderSynced=${wsProvider?.synced}, elapsed=${elapsed1}ms`);

                    // Provider can be null if cleanup ran during the await (navigate away / unmount).
                    if (!wsProvider || isCleaningUp) {
                      return;
                    }

                    // If the relay never reports synced, Y.Text may still be empty while another peer holds
                    // the room (throttled joiner). Solo-seeding then duplicates for them — use observer.
                    // On localhost / flaky relay, synced can stay false even when truly alone; do not send
                    // a solo user back to the loading screen — only block when we see another user.
                    //
                    // IMPORTANT: Use Yjs awareness only here — not Supabase global presence. Presence can
                    // list another user on this filePath before they join the Yjs room (or from another tab),
                    // which blocked the first user from ever seeding Y.Text and deadlocked both clients.
                    if (!wsProvider.synced && ytext.length === 0) {
                      const othersInAwareness = getOtherClients().found;

                      if (othersInAwareness) {
                        logDuplicationDebug('blocking-solo-seed-ws-not-synced', {
                          reason:
                            'wsProvider.synced is still false with empty Y.Text, but another client is in Yjs awareness — observer mode (do not solo-seed).',
                          filePath,
                          elapsedMs: elapsed1,
                          othersInAwareness,
                        });
                        console.log('[DUPLICATION DEBUG] wsProvider not synced after wait — observer mode (do not solo-seed)');
                        setConnectionStatus('syncing-with-others');
                        setEntryGateLoading(true);
                        enterObserverMode('ws-not-synced-after-wait', { allowLeaderSeed: true });
                        return;
                      }

                      logDuplicationDebug('ws-not-synced-solo-continue', {
                        reason:
                          'wsProvider.synced is still false but no other users in presence or awareness — treating as solo/flaky relay; continuing to safety-window seed (no loading gate).',
                        filePath,
                        elapsedMs: elapsed1,
                      });
                      console.log(
                        '[DUPLICATION DEBUG] wsProvider not synced after wait but solo — continuing to normal seed path (not observer)',
                      );
                    }

                    // Re-check awareness: another user may have connected while we waited
                    const { found: othersAfterSync } = getOtherClients();
                    if (othersAfterSync) otherClientsConnected = true;
                    console.log(`[DUPLICATION DEBUG] Post-sync awareness re-check: otherUsers=${otherClientsConnected}, ytextLength=${ytext.length}`);

                    if (ytext.length > 0) {
                      // Content arrived from another user during the sync wait
                      console.log(`[DUPLICATION DEBUG] Yjs has content after sync (${ytext.length} chars) — skipping seed`);
                      shouldPopulateYjs = false;

                    } else if (otherClientsConnected) {
                      // Another user appeared in awareness during the wait but hasn't sent
                      // content yet — switch to observer mode, do NOT seed
                      console.log(`[DUPLICATION DEBUG] Other user appeared post-sync but ytext still empty — observer mode`);
                      setConnectionStatus('syncing-with-others');
                      setEntryGateLoading(true);
                      enterObserverMode('other-user-appeared-post-sync');
                      return;

                    } else {
                      // Yjs awareness can lag Supabase global presence by seconds. If we solo-seed from the
                      // editor (storage snapshot) while another client already holds the room, we push
                      // stale content and undo their live edits — same doc length makes it look like "revert".
                      let othersOnDocPresence = [];
                      try {
                        const supabase = await getSupabase();
                        const { data: { session } } = await supabase.auth.getSession();
                        const currentUserId = session?.user?.id;
                        othersOnDocPresence = Array.from(globalPresenceManager.activeUsers.values()).filter(
                          (u) => u.document_path === filePath && u.user_id !== currentUserId,
                        );
                      } catch (_) {
                        /* ignore */
                      }

                      if (othersOnDocPresence.length > 0) {
                        logDuplicationDebug('blocking-solo-seed-global-presence-others', {
                          reason:
                            'Another client is on this filePath in global presence while Yjs awareness is still empty — wait for remote Y.Text; do not seed from local storage.',
                          otherCount: othersOnDocPresence.length,
                          filePath,
                        });
                        console.log(
                          `[DUPLICATION DEBUG] Global presence shows ${othersOnDocPresence.length} other(s) — waiting for remote Y.Text (no solo seed from editor)`,
                        );
                        setConnectionStatus('syncing-with-others');
                        setEntryGateLoading(true);

                        await waitForYjsTextNonEmpty(ytext, 45000);
                        if (!wsProvider || isCleaningUp) return;

                        if (ytext.length > 0) {
                          console.log(
                            `[DUPLICATION DEBUG] Remote Y.Text arrived after presence wait (${ytext.length} chars) — skipping seed`,
                          );
                          shouldPopulateYjs = false;
                        } else if (getOtherClients().found) {
                          console.log(
                            '[DUPLICATION DEBUG] Awareness shows peer but Y.Text still empty — observer mode',
                          );
                          setConnectionStatus('syncing-with-others');
                          setEntryGateLoading(true);
                          enterObserverMode('presence-others-ytext-still-empty');
                          return;
                        } else {
                          console.log(
                            '[DUPLICATION DEBUG] Remote Y.Text still empty after 20s, no awareness peer — observer (do not storage-seed over possible live room)',
                          );
                          setConnectionStatus('syncing-with-others');
                          setEntryGateLoading(true);
                          enterObserverMode('presence-ghost-no-ytext');
                          return;
                        }
                      }

                      // Yjs still empty and no other users in global presence.
                      // Wait a safety window before seeding — catches concurrent users whose
                      // awareness/content is still in-flight (simultaneous open race).
                      console.log(`[DUPLICATION DEBUG] Yjs empty, no presence others — waiting 600ms safety window before seeding...`);
                      await new Promise((resolve) => setTimeout(resolve, 600));

                      const elapsed2 = Date.now() - t0;
                      const { found: othersAfterWait } = getOtherClients();
                      console.log(`[DUPLICATION DEBUG] Post-safety-window: ytextLength=${ytext.length}, otherUsers=${othersAfterWait}, elapsed=${elapsed2}ms`);

                      if (ytext.length > 0) {
                        console.log(`[DUPLICATION DEBUG] Content arrived in safety window (${ytext.length} chars) — skipping seed`);
                        shouldPopulateYjs = false;
                      } else if (othersAfterWait) {
                        console.log(`[DUPLICATION DEBUG] Other user detected in safety window — observer mode`);
                        setConnectionStatus('syncing-with-others');
                        setEntryGateLoading(true);
                        enterObserverMode('other-user-appeared-in-safety-window');
                        return;
                      } else {
                        console.log(`[DUPLICATION DEBUG] Yjs still empty after safety window — safe to seed (truly solo / new doc)`);
                      }
                    }
                  }

                  // FINAL SAFETY CHECK: never seed if Yjs has content
                  if (ytext.length > 0) {
                    shouldPopulateYjs = false;
                  }

                  // Capture CURRENT editor content now (after all waits) so any images
                  // inserted during the wait window are included in Y.Text population.
                  const currentContent = editor.getContents();
                  const imageOpsCount = currentContent?.ops?.filter(op => op.insert?.image)?.length ?? 0;
                  console.log(`[DUPLICATION DEBUG] Content capture: editorOps=${currentContent?.ops?.length}, imageOps=${imageOpsCount}, shouldPopulate=${shouldPopulateYjs}, ytextLength=${ytext.length}`);

                  // Only populate Yjs if it's still empty and no other users were present
                  if (shouldPopulateYjs && ytext.length === 0 && currentContent && currentContent.ops && currentContent.ops.length > 0) {
                    console.log('[DUPLICATION DEBUG] 📝 Seeding Yjs from editor (solo user or new document)');
                    currentContent.ops.forEach(op => {
                      if (op.insert) {
                        if (typeof op.insert === 'string') {
                          ytext.insert(ytext.length, op.insert, op.attributes || {});
                        } else if (op.insert && typeof op.insert === 'object') {
                          ytext.insertEmbed(ytext.length, op.insert, {});
                        }
                      }
                    });
                    console.log(`[DUPLICATION DEBUG] Seed complete. ytextLength=${ytext.length}`);
                    console.log('[DUPLICATION DEBUG] yjs-seed-from-editor', {
                      path: 'main-solo-or-post-safety-window',
                      ytextLength: ytext.length,
                      editorOps: currentContent?.ops?.length,
                      imageOps: imageOpsCount,
                      filePath,
                    });
                  } else if (!shouldPopulateYjs) {
                    console.log(`[DUPLICATION DEBUG] Using remote Yjs content (${ytext.length} chars) — QuillBinding will update editor`);
                  }

                  console.log(`[DUPLICATION DEBUG] Creating QuillBinding. shouldPopulateYjs=${shouldPopulateYjs}, ytextLength=${ytext.length}, elapsed=${Date.now() - t0}ms`);
                  try {
                    if (wsProvider && wsProvider.awareness) {
                      // Only suppress QuillBinding's setContents when WE just seeded Yjs from the
                      // editor (shouldPopulateYjs=true). Editor and Yjs are identical — setContents
                      // would be a no-op that only causes a visible DOM flicker.
                      //
                      // When shouldPopulateYjs=false (Yjs has remote content), we MUST allow
                      // setContents so QuillBinding syncs the editor to the Yjs state. Suppressing
                      // it leaves the editor on its stale Supabase snapshot; all subsequent remote
                      // deltas then apply to the wrong base → text on wrong lines, words merging.
                      let binding;
                      if (shouldPopulateYjs) {
                        const _originalSetContents = editor.setContents.bind(editor);
                        editor.setContents = () => {};
                        binding = new QuillBinding(ytext, editor, wsProvider.awareness);
                        editor.setContents = _originalSetContents;
                      } else {
                        console.log('[DUPLICATION DEBUG] Allowing QuillBinding setContents — syncing editor to remote Yjs state');
                        binding = new QuillBinding(ytext, editor, wsProvider.awareness);
                      }
                      bindingRef.current = binding;
                      console.log(`[DUPLICATION DEBUG] ✅ QuillBinding created. elapsed=${Date.now() - t0}ms`);
                      logEditorDuplicationRisk('suspected-duplicate-after-main-quill-binding', {
                        shouldPopulateYjs,
                        branch: 'main',
                      });
                      installDuplicateMonitor();
                      clearObserverStuckTimer();

                      setEntryGateLoading(false);
                      hasCompletedInitialJoinRef.current = true;
                      setLocalSyncReady(true);

                      // IMMEDIATE: Update global presence right after binding to capture other users already in document
                      const initialStates = wsProvider.awareness.getStates();
                      if (initialStates.size > 0) {
                        // Extract and cache colors immediately
                        const bindingColors = new Map();
                        initialStates.forEach((state, clientId) => {
                          if (state.user && state.user.color && state.user.id) {
                            bindingColors.set(state.user.id, state.user.color);
                          }
                        });
                        if (bindingColors.size > 0) {
                          globalPresenceManager.cacheUserColors(bindingColors);
                          // Also update setUserColors directly for immediate display
                          setUserColors(prevColors => {
                            const newColors = new Map(prevColors);
                            bindingColors.forEach((color, id) => newColors.set(id, color));
                            return newColors;
                          });
                          console.log('🎨 Immediately cached and set user colors after binding:', bindingColors.size);
                        }

                        if (updateGlobalPresenceRef.current) {
                          console.log('🎨 Immediately updating global presence after QuillBinding creation:', { statesCount: initialStates.size });
                          updateGlobalPresenceRef.current(initialStates);
                        }
                      }

                      // Ensure user awareness is set (in case it wasn't set earlier)
                      // Also update cursor names if they were created before awareness was set
                      setTimeout(async () => {
                        try {
                          if (!wsProvider || !wsProvider.awareness) return;
                          
                          const supabase = await getSupabase();
                          const { data: { session } } = await supabase.auth.getSession();
                          const user = session?.user;
                          
                          if (user && wsProvider && wsProvider.awareness) {
                            const { data: userData } = await supabase
                              .from('users')
                              .select('name')
                              .eq('id', user.id)
                              .single();
                            
                            const userName = userData?.name || user.email || 'Anonymous';

                            // Get current awareness state to check if user is already set
                            const currentState = wsProvider.awareness.getLocalState();
                            if (!currentState?.user || currentState.user.id !== user.id) {
                              // Use existing color from state, cached color, or generate new one
                              // This preserves color for returning users within grace period
                              const existingColor = currentState?.user?.color;
                              const cachedColor = globalPresenceManager.getCachedUserColor(user.id);
                              const localStorageColor = (!cachedColor && !existingColor) ? localStorage.getItem(`userColor_${user.id}`) : null;
                              const userColor = existingColor || cachedColor || localStorageColor || generateNonBlueColor();

                              // Cache the color for future use
                              if (!cachedColor && !existingColor) {
                                globalPresenceManager.cacheUserColor(user.id, userColor);
                              }
                              // Persist to localStorage so color survives page refreshes/new tabs
                              localStorage.setItem(`userColor_${user.id}`, userColor);

                              wsProvider.awareness.setLocalStateField('user', {
                                name: userName,
                                color: userColor,
                                id: user.id
                              });
                              
                              wsProvider.awareness.setLocalStateField('is_typing', false);
                              wsProvider.awareness.setLocalStateField('cursor_active', false);
                              wsProvider.awareness.setLocalStateField('doc_sync_ready', true);
                              
                              // Force awareness update to sync with other clients
                              wsProvider.awareness.emit('update', [{ added: [], updated: [], removed: [] }]);

                              console.log('✅ User awareness confirmed/updated after binding:', { userName, userId: user.id });

                              // IMMEDIATE: Update global presence right away so avatars light up immediately
                              const states = wsProvider.awareness.getStates();
                              if (states.size > 0) {
                                // Extract and cache colors immediately
                                const confirmColors = new Map();
                                states.forEach((state, clientId) => {
                                  if (state.user && state.user.color && state.user.id) {
                                    confirmColors.set(state.user.id, state.user.color);
                                  }
                                });
                                if (confirmColors.size > 0) {
                                  globalPresenceManager.cacheUserColors(confirmColors);
                                  // Also update setUserColors directly for immediate display
                                  setUserColors(prevColors => {
                                    const newColors = new Map(prevColors);
                                    confirmColors.forEach((color, id) => newColors.set(id, color));
                                    return newColors;
                                  });
                                  console.log('🎨 Immediately cached and set user colors after awareness confirm:', confirmColors.size);
                                }

                                if (updateGlobalPresenceRef.current) {
                                  console.log('🎨 Immediately updating global presence after binding:', { statesCount: states.size });
                                  updateGlobalPresenceRef.current(states);
                                }
                              }
                            }
                            
                            // Update any cursor names that might have been created with clientId
                            // This fixes the issue where cursors show "User: 3464010325" instead of name
                            setTimeout(() => {
                              if (!wsProvider || !wsProvider.awareness) return;
                              const states = wsProvider.awareness.getStates();
                              const cursorElements = document.querySelectorAll('.ql-cursor');
                              
                              cursorElements.forEach((cursorEl) => {
                                const cursorName = cursorEl.querySelector('.ql-cursor-name');
                                if (cursorName) {
                                  // Check if cursor name is a clientId (numeric string)
                                  const nameText = cursorName.textContent.trim();
                                  if (/^\d+$/.test(nameText)) {
                                    // This is a clientId, find the matching user from awareness states
                                    for (const [clientId, state] of states) {
                                      if (state.user && clientId.toString() === nameText) {
                                        cursorName.textContent = state.user.name;
                                        if (state.user.color) {
                                          cursorName.style.backgroundColor = state.user.color;
                                          cursorName.style.background = state.user.color;
                                          cursorEl.style.setProperty('border-left-color', state.user.color, 'important');
                                        }
                                        console.log(`✅ Updated cursor name from clientId ${nameText} to ${state.user.name}`);
                                        break;
                                      }
                                    }
                                  }
                                }
                              });
                            }, 200);
                          }
                        } catch (error) {
                          console.error('❌ Error confirming user awareness:', error);
                        }
                      }, 100);
                    }
                  } catch (bindingError) {
                    console.error('❌ Error creating QuillBinding:', bindingError);
                  }
                }
              }, bindingDelay);
            }
          }
        });

        // Handle Yjs sync events for CollaborationDebugPanel telemetry
        wsProvider.on('sync', (synced) => {
          if (isCleaningUp) return;
          
          // Dispatch telemetry for sync events
          // synced=true means we received full state (sync-step1 completed)
          // synced=false can happen during reconnection
          const syncEvent = synced ? 'sync-step1' : 'sync-reconnect';
          dispatchTelemetry('received', syncEvent, 0);
        });

        // Handle awareness updates (for user additions/removals)
        // Throttle to prevent infinite loops
        let lastAwarenessUpdate = 0;
        // Cache to track clientId -> userId mapping for grace period handling on removal
        const clientIdToUserCache = new Map();

        if (wsProvider && wsProvider.awareness) {
          wsProvider.awareness.on('update', ({ added, updated, removed }) => {
            if (isCleaningUp || !wsProvider || !wsProvider.awareness) return;

          const states = wsProvider.awareness.getStates();

          // CRITICAL: Handle removed users IMMEDIATELY (not throttled)
          // This ensures grace period starts right away when users disconnect
          if (removed.length > 0) {
            console.log(`🔄 WebSocket awareness: ${removed.length} user(s) removed`);
            for (const clientId of removed) {
              const cached = clientIdToUserCache.get(clientId);
              if (cached) {
                const cachedUserId = typeof cached === 'string' ? cached : cached.userId;
                
                // Dispatch collab-telemetry event for user-leave
                window.dispatchEvent(new CustomEvent('collab-telemetry', {
                  detail: {
                    type: 'user-leave',
                    userId: cachedUserId,
                    name: cached.name || 'Unknown',
                    ts: Date.now(),
                  }
                }));
                // Never start grace period for the current user - they're navigating away, not "disconnected"
                const currentUser = globalPresenceManager.getCurrentUser();
                if (currentUser && cachedUserId === currentUser.id) {
                  clientIdToUserCache.delete(clientId);
                  continue;
                }
                // Check if user data exists in globalPresenceManager
                let userData = globalPresenceManager.activeUsers.get(cachedUserId);
                if (!userData && typeof cached === 'object') {
                  // User was already removed from activeUsers (e.g. by Supabase presence leave),
                  // but we still need to start a grace period. Construct minimal user data.
                  userData = {
                    user_id: cachedUserId,
                    name: cached.name || cachedUserId,
                    email: cached.email || 'Guest',
                    color: cached.color,
                    source: 'websocket-awareness',
                    is_typing: false,
                    cursor_active: false,
                    last_activity: new Date().toISOString()
                  };
                }
                if (userData && !userData.isStale) {
                  console.log(`⏳ WebSocket user disconnected, starting grace period for: ${userData.name}`);
                  globalPresenceManager.startGracePeriod(cachedUserId, userData);
                }
                clientIdToUserCache.delete(clientId);
              } else {
                console.log(`⚠️ No cached userId for removed clientId: ${clientId}`);
              }
            }
          }

          // Cancel grace only after a short verify — Yjs can emit transient "added" rows on
          // sync/reconnect that are not a real rejoin; immediate cancel was clearing grace
          // within milliseconds of startGracePeriod.
          if (added.length > 0) {
            for (const clientId of added) {
              const state = states.get(clientId);
              if (!state?.user?.id) continue;
              const uid = state.user.id;
              if (!globalPresenceManager.isUserStale(uid)) continue;
              const verifyName = state.user.name;
              setTimeout(() => {
                if (isCleaningUp || !wsProvider || !wsProvider.awareness) return;
                const still = wsProvider.awareness.getStates().get(clientId);
                if (
                  still?.user?.id === uid &&
                  globalPresenceManager.isUserStale(uid)
                ) {
                  console.log(
                    `✅ User ${verifyName} rejoined via WebSocket (verified), cancelling grace period`,
                  );
                  globalPresenceManager.cancelGracePeriod(uid);
                }
              }, 450);
            }
          }

          // CRITICAL: Always update clientId -> userId cache (not throttled)
          // This ensures we have the mapping when users disconnect
          states.forEach((state, clientId) => {
            if (state.user && state.user.id) {
              const isNewUser = !clientIdToUserCache.has(clientId);
              const prevCache = clientIdToUserCache.get(clientId);
              
              clientIdToUserCache.set(clientId, {
                userId: state.user.id,
                name: state.user.name,
                color: state.user.color,
                email: state.user.email
              });
              
              // Dispatch collab-telemetry event for new user-join
              if (isNewUser) {
                window.dispatchEvent(new CustomEvent('collab-telemetry', {
                  detail: {
                    type: 'user-join',
                    userId: state.user.id,
                    name: state.user.name || 'Unknown',
                    color: state.user.color,
                    ts: Date.now(),
                  }
                }));
              }
              
              // Dispatch cursor telemetry when cursor position changes (for remote users)
              if (clientId !== wsProvider.awareness.clientID && state.cursor) {
                // This is a remote user's cursor movement
                dispatchTelemetry('received', 'cursor', 50, state.user.id, { peerTs: Date.now() });
              }
            }
          });

          // Throttle the rest of updates to max once per 500ms
          const now = Date.now();
          if (now - lastAwarenessUpdate < 500) return;
          lastAwarenessUpdate = now;

          // So presence only clears stale when user actually rejoined (was in "added"), not from stale snapshot
          const addedUserIds = new Set(
            added.map((clientId) => states.get(clientId)?.user?.id).filter(Boolean)
          );

          const userColorsMap = new Map();

          states.forEach((state, clientId) => {
            if (state.user) {
              const userId = state.user.id;
              // Prefer cached color so returning users keep same avatar/cursor color after stale
              const color = globalPresenceManager.getCachedUserColor(userId) || state.user.color || '#000000';
              userColorsMap.set(userId, color);

              // Cache last known cursor position for each user (for grace period feature)
              if (state.cursor) {
                let cursorIndex, cursorLength;

                // Handle different cursor formats (Yjs anchor/head or index/length)
                if (state.cursor.anchor !== undefined && state.cursor.head !== undefined) {
                  if (typeof state.cursor.anchor === 'number') {
                    cursorIndex = state.cursor.anchor;
                    cursorLength = (typeof state.cursor.head === 'number' ? state.cursor.head : state.cursor.anchor) - state.cursor.anchor;
                  } else if (state.cursor.anchor?.index !== undefined) {
                    cursorIndex = state.cursor.anchor.index;
                    cursorLength = (state.cursor.head?.index || state.cursor.anchor.index) - state.cursor.anchor.index;
                  }
                } else if (state.cursor.index !== undefined) {
                  cursorIndex = state.cursor.index;
                  cursorLength = state.cursor.length || 0;
                }

                if (cursorIndex !== undefined) {
                  globalPresenceManager.setLastKnownCursor(userId, {
                    index: cursorIndex,
                    length: cursorLength || 0,
                    documentPath: filePath,
                    rawCursor: state.cursor
                  });
                }
              }
            }
          });

          // Cache colors globally so they're available immediately on page load
          if (userColorsMap.size > 0) {
            globalPresenceManager.cacheUserColors(userColorsMap);
          }

          // Update user colors (will be checked in change handler)
          setUserColors(prevColors => {
            const currentColors = Array.from(userColorsMap.entries()).sort();
            const prevColorsArray = Array.from((prevColors || new Map()).entries()).sort();
            if (JSON.stringify(currentColors) !== JSON.stringify(prevColorsArray)) {
              return userColorsMap;
            }
            return prevColors; // Return previous to prevent re-render
          });

          // Update global presence (throttled); pass addedUserIds so we only clear stale for actual re-joins
          if (updateGlobalPresenceRef.current) {
            updateGlobalPresenceRef.current(states, { addedUserIds });
          }

          // Trigger multi-selection highlighting update when users are added/removed
          // This ensures remote selections are updated when users join/leave
          if (added.length > 0 || removed.length > 0 || updated.length > 0) {
            window.dispatchEvent(new CustomEvent('multi-selections-changed', {
              detail: { states: Array.from(states.entries()) }
            }));
          }

          // Note: Grace period cancellation for added users is handled above (before throttle)

          // When new users join, re-broadcast our awareness so they can see our cursor position
          // This fixes the issue where new joiners can't see existing users' cursors until they type
          if (added.length > 0 && wsProvider && wsProvider.awareness) {
            const currentState = wsProvider.awareness.getLocalState();
            if (currentState) {
              console.log('👋 New user joined, re-broadcasting awareness for cursor visibility');
              // Re-set the local state to trigger a broadcast to new users
              setTimeout(() => {
                if (wsProvider && wsProvider.awareness) {
                  // Force re-broadcast by updating a timestamp field
                  wsProvider.awareness.setLocalStateField('last_broadcast', Date.now());
                  // Also re-broadcast user info to ensure colors are synced
                  if (currentState.user) {
                    wsProvider.awareness.setLocalStateField('user', currentState.user);
                  }

                  // Force cursor position re-broadcast by re-setting the Quill selection
                  // This triggers QuillBinding to update cursor awareness
                  try {
                    const quillEditor = editorRef.current?.getEditor?.(0);
                    if (quillEditor) {
                      const currentSelection = quillEditor.getSelection();
                      if (currentSelection) {
                        // Re-set the same selection to trigger cursor awareness update
                        quillEditor.setSelection(currentSelection.index, currentSelection.length, 'silent');
                        console.log('📍 Re-broadcast cursor position for new joiner:', currentSelection);
                      }
                    }
                  } catch (cursorError) {
                    console.warn('⚠️ Could not re-broadcast cursor position:', cursorError);
                  }
                }
              }, 100);
            }
          }
          });
        }

        // Handle awareness changes (for cursor positions and color updates)
        // Throttle to prevent infinite loops
        let lastAwarenessChange = 0;
        if (wsProvider && wsProvider.awareness) {
          wsProvider.awareness.on('change', (changes) => {
            if (isCleaningUp || !wsProvider || !wsProvider.awareness) return;
          
          // Throttle changes to max once per 300ms
          const now = Date.now();
          if (now - lastAwarenessChange < 300) return;
          lastAwarenessChange = now;
          
          const states = wsProvider.awareness.getStates();
          const newUserColors = new Map();
          
          // Update user colors from awareness states and cache last known cursor positions
          states.forEach((state, clientId) => {
            if (state.user && state.user.color) {
              const userId = state.user.id;
              // Prefer cached color so returning users within grace period keep their original color
              const color = globalPresenceManager.getCachedUserColor(userId) || state.user.color;
              newUserColors.set(userId, color);
            }

            // Cache last known cursor position for each user (for grace period feature)
            if (state.user && state.user.id && state.cursor) {
              let cursorIndex, cursorLength;

              // Handle different cursor formats (Yjs anchor/head or index/length)
              if (state.cursor.anchor !== undefined && state.cursor.head !== undefined) {
                // Try to get index from anchor if available
                if (typeof state.cursor.anchor === 'number') {
                  cursorIndex = state.cursor.anchor;
                  cursorLength = (typeof state.cursor.head === 'number' ? state.cursor.head : state.cursor.anchor) - state.cursor.anchor;
                } else if (state.cursor.anchor?.index !== undefined) {
                  cursorIndex = state.cursor.anchor.index;
                  cursorLength = (state.cursor.head?.index || state.cursor.anchor.index) - state.cursor.anchor.index;
                }
              } else if (state.cursor.index !== undefined) {
                cursorIndex = state.cursor.index;
                cursorLength = state.cursor.length || 0;
              }

              if (cursorIndex !== undefined) {
                globalPresenceManager.setLastKnownCursor(state.user.id, {
                  index: cursorIndex,
                  length: cursorLength || 0,
                  documentPath: filePath,
                  rawCursor: state.cursor // Store raw cursor for Yjs conversion if needed
                });
              }
            }
          });

          // Cache colors globally so they're available immediately on page load
          if (newUserColors.size > 0) {
            globalPresenceManager.cacheUserColors(newUserColors);
          }

          // Update user colors (functional update to avoid dependency)
          setUserColors(prevColors => {
            const currentColors = Array.from(newUserColors.entries()).sort();
            const prevColorsArray = Array.from((prevColors || new Map()).entries()).sort();
            if (JSON.stringify(currentColors) !== JSON.stringify(prevColorsArray)) {
              return newUserColors;
            }
            return prevColors; // Return previous to prevent re-render
          });
          
          // Update global presence (throttled)
          if (updateGlobalPresenceRef.current) {
            updateGlobalPresenceRef.current(states);
          }
          
          // Broadcast ai_doc_processing_locked state so non-locking clients can react
          let isDocLocked = false;
          states.forEach((state) => {
            if (state.ai_doc_processing_locked === true) isDocLocked = true;
          });
          window.dispatchEvent(new CustomEvent('ai-doc-processing-lock-changed', {
            detail: { locked: isDocLocked }
          }));

          // Check if any multi_selections changed and trigger highlighting update
          let multiSelectionsChanged = false;
          states.forEach((state) => {
            if (state.multi_selections && Array.isArray(state.multi_selections) && state.multi_selections.length > 0) {
              multiSelectionsChanged = true;
            }
          });
          
          // Dispatch custom event to trigger multi-selection highlighting update
          if (multiSelectionsChanged || changes.updated.some(clientId => {
            const state = states.get(clientId);
            return state && state.multi_selections !== undefined;
          })) {
            // Dispatch custom event to trigger highlighting update in EditableDocumentPage
            window.dispatchEvent(new CustomEvent('multi-selections-changed', {
              detail: { states: Array.from(states.entries()) }
            }));
          }
          
          // Apply user colors to cursor elements in DOM (debounced)
          clearTimeout(window.__cursorColorTimeout);
          window.__cursorColorTimeout = setTimeout(() => {
            const cursorElements = document.querySelectorAll('.ql-cursor');
            cursorElements.forEach((cursorEl) => {
              const cursorName = cursorEl.querySelector('.ql-cursor-name');
              if (cursorName) {
                const nameText = cursorName.textContent.trim();
                
                // CRITICAL: Fix cursor names that show clientId instead of user name
                // This happens when cursors are created before user awareness is set (slow networks)
                if (/^\d+$/.test(nameText)) {
                  // This is a clientId, find the matching user from awareness states
                  for (const [clientId, state] of states) {
                    if (state.user && clientId.toString() === nameText) {
                      cursorName.textContent = state.user.name;
                      if (state.user.color) {
                        cursorName.style.backgroundColor = state.user.color;
                        cursorName.style.background = state.user.color;
                        cursorEl.style.setProperty('border-left-color', state.user.color, 'important');
                      }
                      console.log(`✅ Fixed cursor name from clientId ${nameText} to ${state.user.name}`);
                      break;
                    }
                  }
                } else {
                  // Find matching user by name and apply colors
                for (const [clientId, state] of states) {
                    if (state.user && state.user.name === nameText) {
                    // Apply user color to cursor tag background
                      if (state.user.color) {
                    cursorName.style.backgroundColor = state.user.color;
                    cursorName.style.background = state.user.color;
                    
                    // Make the cursor border match the tag color
                    cursorEl.style.setProperty('border-left-color', state.user.color, 'important');
                    
                    // Also target any cursor caret elements
                    const cursorCarets = cursorEl.querySelectorAll('.ql-cursor-caret');
                    cursorCarets.forEach(caret => {
                      caret.style.setProperty('border-left-color', state.user.color, 'important');
                    });
                      }
                    break;
                    }
                  }
                }
              }
            });
            
            // Handle highlighted user cursor (access via closure, not dependency)
            // This will be handled by the parent component's highlightedUser state
            // We'll use a custom event or ref to communicate this
          }, 500); // Increased delay to reduce frequency
          });
        }
        
        // Periodic check to fix cursor names (for slow network scenarios)
        // This ensures cursor names are updated even if awareness updates are delayed
        cursorNameFixInterval = setInterval(() => {
          if (isCleaningUp || !wsProvider || !wsProvider.awareness) {
            clearInterval(cursorNameFixInterval);
            return;
          }
          
          try {
            const states = wsProvider.awareness.getStates();
            const cursorElements = document.querySelectorAll('.ql-cursor');
            
            cursorElements.forEach((cursorEl) => {
              const cursorName = cursorEl.querySelector('.ql-cursor-name');
              if (cursorName) {
                const nameText = cursorName.textContent.trim();
                
                // Fix cursor names showing clientId
                if (/^\d+$/.test(nameText)) {
                  for (const [clientId, state] of states) {
                    if (state.user && clientId.toString() === nameText) {
                      cursorName.textContent = state.user.name;
                      if (state.user.color) {
                        cursorName.style.backgroundColor = state.user.color;
                        cursorName.style.background = state.user.color;
                        cursorEl.style.setProperty('border-left-color', state.user.color, 'important');
                      }
                      break;
                    }
                  }
                }
              }
            });
          } catch (error) {
            // Silently handle errors in periodic check
          }
        }, 2000); // Check every 2 seconds
        
        // Store interval for cleanup
        if (!window.__cursorNameFixIntervals) {
          window.__cursorNameFixIntervals = [];
        }
        window.__cursorNameFixIntervals.push(cursorNameFixInterval);

        // Note: Save on disconnect is handled by the main status handler above

      } catch (error) {
        console.error('❌ Error setting up WebSocket:', error);
        setIsConnected(false);
        websocketSetupLock.current = false;
      }
    };

    // For FFP/FFS documents, content is loaded asynchronously from last visit
    // The useEffect will automatically re-run when contentLoadingComplete becomes true
    // So we can just call setupWebSocket directly - it will only run if all conditions are met
    setupWebSocket();

    // Cleanup function
    return () => {
      // Reduced logging
      isCleaningUp = true;
      
      // Clean up cursor name fix interval
      if (cursorNameFixInterval) {
        clearInterval(cursorNameFixInterval);
        cursorNameFixInterval = null;
      }
      
      // Also clean up from global array if it exists
      if (window.__cursorNameFixIntervals && Array.isArray(window.__cursorNameFixIntervals)) {
        window.__cursorNameFixIntervals = window.__cursorNameFixIntervals.filter(
          interval => interval !== cursorNameFixInterval
        );
      }
      
      if (typingCleanupInterval) {
        clearInterval(typingCleanupInterval);
      }
      
      if (connectionTimeout) {
        clearTimeout(connectionTimeout);
      }

      clearObserverStuckTimer();

      if (duplicateMonitorCleanup) {
        duplicateMonitorCleanup();
        duplicateMonitorCleanup = null;
      }
      
      if (bindingRef.current && typeof bindingRef.current.destroy === 'function') {
        bindingRef.current.destroy();
      }
      bindingRef.current = null;
      
      if (wsProvider) {
        try {
          // Remove all event listeners before destroying
          if (wsProvider.awareness) {
          wsProvider.awareness.off('update');
          wsProvider.awareness.off('change');
          }
          wsProvider.off('status');
          wsProvider.destroy();
        } catch (error) {
          console.error('❌ Error destroying WebSocket provider:', error);
        }
        wsProvider = null;
        setProvider(null);
        window.wsProvider = null; // Clear global reference
      }
      
      // Reset flags
      websocketSetupLock.current = false;
      window.__wsSetupLogged = false;
      window.__yjsPopulated = false;
      if (window.__cursorColorTimeout) {
        clearTimeout(window.__cursorColorTimeout);
      }
    };
  }, [
    // Only include stable dependencies to prevent infinite loops
    filePath,
    documentLoaded,
    contentLoadingComplete,
    correctedType,
    // Exclude functions and refs that change on every render
    // editorRef, setIsConnected, setConnectionStatus, setUserColors, 
    // handleSaveDoc, updateGlobalPresenceFromAwareness, websocketSetupLock, highlightedUser
  ]);

  // Initialize typing detection and active document tracking
  useEffect(() => {
    const initializeTracking = async () => {
      const editor = editorRef.current?.getEditor(0);
      if (!editor || !documentLoaded) {
        return;
      }

      try {
        const supabase = await getSupabase();
        const { data: { session } } = await supabase.auth.getSession();
        const user = session?.user;
        
        if (user) {
          const { data: userData } = await supabase
            .from('users')
            .select('name')
            .eq('id', user.id)
            .single();
          
          const userName = userData?.name || user.email || 'Anonymous';
          
          await typingDetection.initialize(editor, filePath, user.id, userName);
          await activeDocumentTracker.initialize();
          
          await activeDocumentTracker.trackDocument(
            filePath,
            validId,
            extractedName,
            user.id,
            userName
          );
        }
      } catch (error) {
        console.error('❌ Error initializing tracking:', error);
      }
    };

    initializeTracking();

    return () => {
      // Non-blocking cleanup - don't wait for async operations
      typingDetection.cleanup();
      // Fire and forget - don't block navigation
      activeDocumentTracker.untrackDocument(filePath).catch(error => {
        console.error('❌ Error untracking document during cleanup:', error);
      });
    };
  }, [filePath, documentLoaded, validId, extractedName]);

  // Initialize globalPresenceManager tracking
  useEffect(() => {
    const initializeGlobalPresence = async () => {
      try {
        const supabase = await getSupabase();
        const { data: { session } } = await supabase.auth.getSession();
        const user = session?.user;
        
        if (user) {
          const { data: userData } = await supabase
            .from('users')
            .select('name')
            .eq('id', user.id)
            .single();
          
          const userName = userData?.name || user.email || 'Anonymous';
          
          if (!globalPresenceManager.isInitialized) {
            await globalPresenceManager.initialize();
          }
          
          // Use actualPatientId (the real patient UUID) if available, otherwise fall back to validId
          const patientIdForTracking = actualPatientId || validId;
          console.log('🔵 Global presence tracking with patient ID:', { actualPatientId, validId, patientIdForTracking });

          await globalPresenceManager.trackUser(
            filePath,
            patientIdForTracking,
            extractedName,
            false,
            false,
            scheduleDate || ''
          );
        }
      } catch (error) {
        console.error('❌ Error initializing global presence:', error);
      }
    };

    initializeGlobalPresence();

    return () => {
      // Non-blocking cleanup - don't wait for async operations
      try {
        globalPresenceManager.removeCurrentUser();
      } catch (error) {
        console.error('❌ Error removing current user during cleanup:', error);
      }
    };
  }, [filePath, validId, actualPatientId, extractedName, scheduleDate]);

  // Cleanup versioning system on unmount
  useEffect(() => {
    return () => {
      if (versioningSystem) {
        versioningSystem.destroy();
      }
    };
  }, [versioningSystem]);

  return {
    provider,
    bindingRef,
    versioningSystem,
  };
}

